import { ref, onBeforeUnmount } from 'vue'
import type { Ref } from 'vue'
import dayjs from 'dayjs'
import { debounce } from 'lodash'
import {
  updateAccountBill,
  batchUpdateAccountBill,
  batchUpdateExpectedReceiptTime,
  queryExchangeRate,
  type AccountBillItem,
  type AccountBillUpdateParams,
} from '@/api/billApi'
import { message } from 'ant-design-vue'
import { OK } from '@/common'

export type ReceivedStatus = 0 | 10 | 20 | 30

export type EditingField =
  | 'expectedReceiptTime'
  | 'actualReceivedAmount'
  | 'actualReceiptTime'
  | 'balance'
  | 'receivedStatus'
  | 'withholdingTax'
  | null

type EmitType = {
  (event: 'update-list'): void
}

export const useEditField = ({
  props,
  emit,
  isBatchOperation,
  selectedRows,
  selectedRowKeys,
  currentRecord,
  payChnl,
}: {
  props: { data: AccountBillItem[] }
  emit: EmitType
  isBatchOperation: Ref<boolean>
  selectedRows: Ref<AccountBillItem[]>
  selectedRowKeys: Ref<string[]>
  currentRecord: Ref<AccountBillItem>
  payChnl: Ref<string>
}) => {
  const editFieldModalVisible = ref(false)
  const editingField = ref<EditingField>(null)
  const editingValue = ref<dayjs.Dayjs | number | null>(null)
  const editingMonth = ref<dayjs.Dayjs | null>(null)
  const editFieldTitle = ref('')
  const usdValue = ref('')
  const usdValueUnit = ref('')

  const handleEditField = async (record: AccountBillItem, field: Exclude<EditingField, 'receivedStatus' | null>) => {
    isBatchOperation.value = false
    currentRecord.value = { ...record }
    editingField.value = field
    if (field === 'actualReceiptTime') {
      editingValue.value = record[field] ? dayjs(record[field]) : null
    } else if (field === 'expectedReceiptTime') {
      editingMonth.value = null
      editingValue.value = null
    } else {
      editingValue.value = record[field] && typeof record[field] === 'string' ? parseFloat(record[field]) : null
      if (field === 'withholdingTax') {
        usdValueUnit.value = record.currency
        usdValue.value = ''
      }
    }
    editFieldTitle.value =
      field === 'expectedReceiptTime'
        ? '预计收款时间'
        : field === 'actualReceiptTime'
        ? '实际收款时间'
        : field === 'balance'
        ? '上月余额'
        : field === 'actualReceivedAmount'
        ? '实际收款金额'
        : '预扣税'
    editFieldModalVisible.value = true
  }

  const handleEditFieldConfirm = async () => {
    if (editingField.value === 'expectedReceiptTime') {
      if (!editingMonth.value || !editingValue.value) {
        return message.warning('请选择月份和日期')
      }
      try {
        const month = (editingMonth.value as dayjs.Dayjs).format('YYYY-MM')
        const res = await batchUpdateExpectedReceiptTime({
          payChnl: payChnl.value,
          startMonthDate: month,
          endMonthDate: month,
          expectedReceiptTime: (editingValue.value as dayjs.Dayjs).format('YYYY-MM-DD'),
        })
        if (res && res.code === OK) {
          message.success('更新成功')
          emit('update-list')
        }
      } catch (error) {
        console.log('[ error ] >', error)
      }
      editFieldModalVisible.value = false
      return
    }

    if (isBatchOperation.value && selectedRows.value.length > 0 && editingField.value && editingValue.value !== null) {
      try {
        const ids = selectedRows.value.map((record) => record.id)
        let formattedValue: string | null = null
        if (editingField.value === 'actualReceiptTime') {
          formattedValue = (editingValue.value as dayjs.Dayjs).format('YYYY-MM-DD')
        } else {
          formattedValue = String(editingValue.value)
        }
        const updateParams: any = { ids }
        if (editingField.value === 'actualReceivedAmount') {
          updateParams.actualReceivedAmount = formattedValue
        } else if (editingField.value === 'actualReceiptTime') {
          updateParams.actualReceiptTime = formattedValue
        } else if (editingField.value === 'balance') {
          updateParams.balance = formattedValue
        } else if (editingField.value === 'receivedStatus') {
          updateParams.receivedStatus = Number(formattedValue) as ReceivedStatus
        }
        const res = await batchUpdateAccountBill(updateParams)
        if (res && res.code === OK) {
          selectedRows.value.forEach((record) => {
            if (editingField.value === 'actualReceivedAmount') {
              record.actualReceivedAmount = formattedValue as string
            } else if (editingField.value === 'actualReceiptTime') {
              record.actualReceiptTime = formattedValue as string
            } else if (editingField.value === 'balance') {
              record.balance = formattedValue as string
            } else if (editingField.value === 'receivedStatus') {
              record.receivedStatus = Number(formattedValue) as ReceivedStatus
            }
          })
          message.success(`成功更新${selectedRows.value.length}条记录`)
          emit('update-list')
          selectedRowKeys.value = []
        }
      } catch (error) {
        console.log('[ error ] >', error)
      }
    } else if (currentRecord.value && editingField.value && editingValue.value !== null) {
      try {
        const updateParams: AccountBillUpdateParams = { id: currentRecord.value.id }
        if (editingField.value === 'actualReceivedAmount') {
          updateParams.actualReceivedAmount = String(editingValue.value)
        } else if (editingField.value === 'actualReceiptTime') {
          updateParams.actualReceiptTime = (editingValue.value as dayjs.Dayjs).format('YYYY-MM-DD')
        } else if (editingField.value === 'balance') {
          updateParams.balance = String(editingValue.value)
        } else if (editingField.value === 'receivedStatus') {
          updateParams.receivedStatus = Number(editingValue.value) as ReceivedStatus
        } else if (editingField.value === 'withholdingTax') {
          updateParams.withholdingTax = String(editingValue.value)
        }
        const res = await updateAccountBill(updateParams)
        if (res && res.code === OK) {
          const record = props.data.find((item) => item.id === currentRecord.value?.id)
          if (record) {
            if (editingField.value === 'actualReceivedAmount') {
              record.actualReceivedAmount = updateParams.actualReceivedAmount ?? record.actualReceivedAmount
            } else if (editingField.value === 'actualReceiptTime') {
              record.actualReceiptTime = updateParams.actualReceiptTime ?? record.actualReceiptTime
            } else if (editingField.value === 'balance') {
              record.balance = updateParams.balance ?? record.balance
            } else if (editingField.value === 'receivedStatus') {
              record.receivedStatus = updateParams.receivedStatus ?? record.receivedStatus
            } else if (editingField.value === 'withholdingTax') {
              record.actualReceivedAmount = updateParams.actualReceivedAmount ?? record.actualReceivedAmount
            }
          }
          emit('update-list')
          message.success('更新成功')
        }
      } catch (error) {
        console.log('[ error ] >', error)
      }
    }
    editFieldModalVisible.value = false
  }

  const handleEditFieldCancel = () => {
    editFieldModalVisible.value = false
  }

  const handleQueryExchangeRate = async (amount?: number | null) => {
    void amount
    try {
      const res = await queryExchangeRate({
        fromCurrency: 'BRL',
        toCurrency: usdValueUnit.value,
        amount: usdValue.value,
        rateSourceType: 1,
        rateDate: currentRecord.value.billMonth + '-01',
      })
      if (res && res.code === OK && res.data) {
        editingValue.value = res.data.convertedAmount
      }
    } catch (error) {
      console.log('[ error ] >', error)
      editingValue.value = null
    }
  }

  const debouncedQueryExchangeRate = debounce(handleQueryExchangeRate, 500)

  onBeforeUnmount(() => {
    debouncedQueryExchangeRate.cancel()
  })

  const handleWithholdingTaxChange = (value: number | null) => {
    if (editingField.value === 'withholdingTax') {
      if (typeof value === 'number') {
        debouncedQueryExchangeRate(value)
      } else {
        debouncedQueryExchangeRate.cancel()
      }
    }
  }

  return {
    editFieldModalVisible,
    editingField,
    editingValue,
    editingMonth,
    editFieldTitle,
    usdValue,
    usdValueUnit,
    handleEditField,
    handleEditFieldConfirm,
    handleEditFieldCancel,
    handleQueryExchangeRate,
    debouncedQueryExchangeRate,
    handleWithholdingTaxChange,
  }
}
